import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "What services do you offer?",
  "How does the 30-day free trial work?",
  "What free tools do you have?",
  "How do I hire Julius?",
];

// Renders assistant text with markdown links (routed internally) and **bold**.
function renderRich(text) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      const cls = "text-primary font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity";
      return href.startsWith("/") ? (
        <Link key={i} to={href} data-testid="chat-msg-link" className={cls}>{label}</Link>
      ) : (
        <a key={i} href={href} target="_blank" rel="noreferrer" data-testid="chat-msg-link" className={cls}>{label}</a>
      );
    }
    return part.split(/(\*\*[^*]+\*\*)/g).map((bp, j) => {
      const bold = bp.match(/^\*\*([^*]+)\*\*$/);
      return bold ? <strong key={`${i}-${j}`}>{bold[1]}</strong> : <React.Fragment key={`${i}-${j}`}>{bp}</React.Fragment>;
    });
  });
}

function sessionId() {
  let s = localStorage.getItem("jm_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem("jm_chat_session", s);
  }
  return s;
}

/**
 * Chatbot — Julius's assistant window. Controlled component: the mascot
 * (SiteGuide) owns the open/close state and is the only entry point.
 */
export default function Chatbot({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Julius's assistant. Ask me anything about his services, pricing, free tools, or how he can help your business grow." },
  ]);
  const [suggestions, setSuggestions] = useState(SUGGESTIONS);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  // Suggestion chips distilled from real visitor questions (falls back to static).
  useEffect(() => {
    fetch(`${API}/chat/suggestions`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d.suggestions) && d.suggestions.length) setSuggestions(d.suggestions);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble. Please email julius.a.mason@gmail.com." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="chatbot-window"
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-24 left-5 z-[150] w-[calc(100vw-3rem)] max-w-sm h-[540px] max-h-[70vh] bg-card border border-border flex flex-col shadow-2xl"
        >
          <div className="p-4 border-b border-border flex items-center gap-3">
            <div className="w-9 h-9 rounded-full overflow-hidden border border-primary bg-[#FAF7F2] flex-shrink-0">
              <img src="/mascot.png" alt="" className="w-full h-full object-cover" data-i18n-skip="true" />
            </div>
            <div className="flex-1">
              <p className="font-display text-lg leading-none">Chat with Julius</p>
              <p className="text-xs text-muted-foreground mt-1">Services, pricing, tools — ask anything</p>
            </div>
            <button onClick={onClose} aria-label="Close chat" data-testid="chat-close"
              className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-primary text-white" : "bg-secondary text-foreground"
                  }`}
                >
                  {m.role === "assistant"
                    ? (m.content ? renderRich(m.content) : (busy && i === messages.length - 1 ? "…" : ""))
                    : m.content}
                </div>
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="space-y-2 pt-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="chat-suggestion"
                    className="block w-full text-left text-xs px-3 py-2 border border-border hover:border-primary hover:text-primary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-border flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 bg-secondary px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
            />
            <button data-testid="chat-send" type="submit" disabled={busy} className="p-2.5 bg-primary text-white disabled:opacity-50 hover:bg-primary/90 active:scale-95 transition">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
